import type { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import {
	type ExistingProvider,
	inject,
	InjectionToken,
	type InputSignal,
	type ModelSignal,
	type Signal,
	type Type,
	type ValueProvider,
} from '@angular/core';
import type { ControlState } from '@spartan-ng/brain/forms';
import type { BrnComboboxChipInput } from './brn-combobox-chip-input';
import { comboboxContainsFilter } from './brn-combobox-filter';
import type { BrnComboboxInput } from './brn-combobox-input';
import type { BrnComboboxItem } from './brn-combobox-item';
import type { BrnComboboxList } from './brn-combobox-list';

export type ComboboxInputMode = 'combobox' | 'popup';

export interface BrnComboboxBase<T> {
	filter: InputSignal<ComboboxFilter<T>>;
	itemToString: InputSignal<ComboboxItemToString<T> | undefined>;
	collator: Signal<Intl.Collator>;
	search: ModelSignal<string>;
	disabled: Signal<boolean>;
	disabledState: Signal<boolean>;
	keyManager: ActiveDescendantKeyManager<BrnComboboxItem<T>>;
	value: ModelSignal<T | undefined | null> | ModelSignal<T[] | undefined | null>;
	visibleItems: Signal<boolean>;
	isExpanded: Signal<boolean>;
	searchInputWrapperWidth: Signal<number | null>;
	mode: Signal<ComboboxInputMode>;
	controlState?: Signal<ControlState | null>;
	hasValue: Signal<boolean>;
	listId: Signal<string | undefined>;
	contentId: Signal<string | undefined>;

	isSelected: (itemValue: T) => boolean;
	select: (itemValue: T) => void;
	open: () => void;
	resetValue: () => void;
	resetSearch: () => void;
	/** Select the active item with Enter key. */
	selectActiveItem: () => void;
	/** Close the combobox popup. */
	close: () => void;
	/** Remove last selected item with Backspace key. Only works with multiple selection comboboxes. */
	removeLastSelectedItem: () => void;
	removeValue: (value: T) => void;
	/** Register the combobox input component for single selection mode */
	registerComboboxInput?: (input: BrnComboboxInput<T>) => void;
	/** Register the combobox chip input component for multi selection mode */
	registerComboboxChipInput?: (input: BrnComboboxChipInput<T>) => void;
	/** Register the combobox list component so the input can reference its id via aria-controls */
	registerComboboxList?: (list: BrnComboboxList) => void;
	updateInputWidth: (width: number | null) => void;
}

export const BrnComboboxBaseToken = new InjectionToken<BrnComboboxBase<unknown>>('BrnComboboxBaseToken');

export function provideBrnComboboxBase<T>(instance: Type<BrnComboboxBase<T>>): ExistingProvider {
	return { provide: BrnComboboxBaseToken, useExisting: instance };
}

/**
 * Inject the combobox component.
 */
export function injectBrnComboboxBase<T>(): BrnComboboxBase<T> {
	return inject(BrnComboboxBaseToken) as BrnComboboxBase<T>;
}

// config
export interface ComboboxFilterOptions extends Intl.CollatorOptions {
	/**
	 * The locale to use for string comparison.
	 * Defaults to the user's runtime locale.
	 */
	locale?: Intl.LocalesArgument;
}

export type ComboboxFilter<T> = (
	itemValue: T,
	search: string,
	collator: Intl.Collator,
	itemToString?: ComboboxItemToString<T>,
) => boolean;

export type ComboboxItemEqualToValue<T> = (itemValue: T, selectedValue: T | undefined | null) => boolean;

export type ComboboxItemToString<T> = (itemValue: T) => string;

export interface BrnComboboxConfig<T> {
	filterOptions: ComboboxFilterOptions;
	filter: ComboboxFilter<T>;
	isItemEqualToValue: ComboboxItemEqualToValue<T>;
	/**
	 * Determines whether a value is present (i.e., has been selected).
	 * Considers `undefined`, `null`, and empty string as "not present".
	 * Used by the single variant to check if the selected value is present,
	 * and by both single and multi variants to validate an item value on Enter key selection.
	 */
	isSingleValuePresent: (value: T | undefined | null) => boolean;
	itemToString?: ComboboxItemToString<T>;
	autoHighlight: boolean;
	/** Whether to reset the search and close the popover after selecting an item when the search is active. */
	closeOnSelect: boolean;
}

function getDefaultConfig<T>(): BrnComboboxConfig<T> {
	return {
		filterOptions: {
			usage: 'search',
			sensitivity: 'base',
			ignorePunctuation: true,
		},
		filter: (itemValue: T, search: string, collator: Intl.Collator, itemToString?: ComboboxItemToString<T>) =>
			comboboxContainsFilter(itemValue, search, collator, itemToString),
		isItemEqualToValue: (itemValue: T, selectedValue: T | undefined | null) => Object.is(itemValue, selectedValue),
		isSingleValuePresent: (value: T | undefined | null) => value !== undefined && value !== null && value !== '',
		itemToString: undefined,
		autoHighlight: false,
		closeOnSelect: true,
	};
}

const BrnComboboxConfigToken = new InjectionToken<BrnComboboxConfig<unknown>>('BrnComboboxConfig');

export function provideBrnComboboxConfig<T>(config: Partial<BrnComboboxConfig<T>>): ValueProvider {
	return { provide: BrnComboboxConfigToken, useValue: { ...getDefaultConfig(), ...config } };
}

export function injectBrnComboboxConfig<T>(): BrnComboboxConfig<T> {
	const injectedConfig = inject(BrnComboboxConfigToken, { optional: true });
	return injectedConfig ? (injectedConfig as BrnComboboxConfig<T>) : getDefaultConfig();
}
